import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  ChevronLeft, ChevronRight, Star, Clock, Users, MapPin, ArrowRight,
  ShieldCheck, Zap, Calendar, Heart, Share2,
  CheckCircle2, XCircle, Quote, Camera, BookOpen,
  Sunrise, TrendingUp, Wind, Umbrella, Landmark, Utensils, Navigation,
  Plus, Minus,
  Compass, Leaf, Award, Mountain, AlertCircle, Waves, Building2,
} from 'lucide-react';
import { GET_TOUR_EXPEDITION } from '../graphql/tours';
import { TRACK_PAGE_VIEW } from '../graphql/analytics';
import { getVisitorSessionId } from '../lib/visitorSession';

/* ─── Design tokens ─── */
const G  = '#B8975A';
const GD = 'rgba(184,151,90,0.13)';
const GM = 'rgba(184,151,90,0.32)';
const N  = '#080e1a';
const CR = '#F5F3EF';

const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`;

/* ─── Icon registry ─── */
const ICON_MAP = {
  Camera, Compass, Leaf, Award, Sunrise, TrendingUp,
  Utensils, Navigation, Landmark, Mountain, Waves, Building2,
  ShieldCheck, Star, Users, Zap, Wind, Umbrella, BookOpen,
};
const getIcon = (name) => ICON_MAP[name] || Compass;

/* ─── Language codes ─── */
const LANG_LABELS = { en:'English', sw:'Swahili', fr:'French', de:'German', es:'Spanish', it:'Italian' };
const langLabel = (code) => LANG_LABELS[code] || code.toUpperCase();

/* ─── Day category → icon ─── */
const CAT_ICONS = {
  wildlife: TrendingUp, nature: Leaf, adventure: Mountain,
  cultural: Landmark, gastronomy: Utensils, leisure: Sunrise,
  meeting: Navigation,
};

const S = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');
    *,*::before,*::after{box-sizing:border-box}
    .fc{font-family:'Cormorant Garamond',serif} .fj{font-family:'Jost',sans-serif}
    @keyframes riseIn{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    .ri{animation:riseIn .9s cubic-bezier(.22,1,.36,1) both}
    .fi{animation:fadeIn .7s ease both}
    .d1{animation-delay:.08s} .d2{animation-delay:.18s} .d3{animation-delay:.3s}
    .page-shell{display:flex;min-height:100vh}
    .chapter-rail{display:none;width:72px;flex-shrink:0;position:sticky;top:76px;height:calc(100vh - 76px);background:${N};flex-direction:column;align-items:center;justify-content:space-between;padding:24px 0;z-index:80;border-right:1px solid rgba(184,151,90,.12);overflow:hidden;align-self:flex-start}
    @media(min-width:900px){.chapter-rail{display:flex}}
    .rail-logo{writing-mode:vertical-rl;text-orientation:mixed;transform:rotate(180deg);font-family:'Jost',sans-serif;font-size:12px;font-weight:600;letter-spacing:0.2em;text-transform:uppercase;color:rgba(255,255,255,.25)}
    .rail-dots{display:flex;flex-direction:column;align-items:center;gap:0}
    .rail-dot-wrap{display:flex;flex-direction:column;align-items:center;cursor:pointer;padding:10px 0;position:relative}
    .rail-dot{width:6px;height:6px;border-radius:0;background:rgba(255,255,255,.18);transition:all .3s;transform:rotate(45deg)}
    .rail-dot-wrap:hover .rail-dot{background:${G}} .rail-dot-wrap.active .rail-dot{background:${G};width:8px;height:8px}
    .rail-dot-wrap::after{content:attr(data-label);position:fixed;left:84px;top:50%;transform:translateY(-50%);font-family:'Jost',sans-serif;font-size:12px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;color:${G};background:${N};padding:4px 10px;border:1px solid ${GM};white-space:nowrap;pointer-events:none;opacity:0;transition:opacity .2s;z-index:10030}
    .rail-dot-wrap:hover::after{opacity:1}
    .rail-connector{width:1px;height:20px;background:rgba(255,255,255,.07)}
    .rail-num{font-family:'Cormorant Garamond',serif;font-size:15px;font-weight:300;color:rgba(255,255,255,.2)}
    .main{flex:1;overflow:hidden}
    .mob-nav{position:sticky;top:0;z-index:70;background:rgba(8,14,26,.97);backdrop-filter:blur(18px);border-bottom:1px solid rgba(184,151,90,.15)}
    .mob-nav-inner{display:flex;overflow-x:auto;white-space:nowrap;scrollbar-width:none;padding:0 16px}
    .mob-nav-inner::-webkit-scrollbar{display:none}
    @media(min-width:640px){.mob-nav-inner{justify-content:center;overflow-x:visible;padding:0 28px}}
    @media(min-width:900px){.mob-nav{display:none}}
    .mnav-btn{font-family:'Jost',sans-serif;font-size:12px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;padding:14px;flex-shrink:0;color:rgba(255,255,255,.35);border-bottom:2px solid transparent;background:transparent;cursor:pointer;transition:all .25s}
    .mnav-btn:hover{color:rgba(255,255,255,.7)} .mnav-btn.on{color:${G};border-bottom-color:${G}}
    .hero{position:relative;height:72vh;min-height:480px;background:${N};overflow:hidden}
    .hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
    .hero-grad{position:absolute;inset:0;background:linear-gradient(to bottom,rgba(8,14,26,.7) 0%,rgba(8,14,26,.2) 40%,rgba(8,14,26,.96) 100%)}
    .hero-grain{position:absolute;inset:0;background-image:${GRAIN};pointer-events:none;opacity:.5}
    .hero-topbar{position:absolute;top:0;left:0;right:0;z-index:20;display:flex;justify-content:space-between;align-items:center;padding:24px 20px}
    @media(min-width:640px){.hero-topbar{padding:28px 32px}}
    .hero-body{position:absolute;bottom:0;left:0;right:0;z-index:20;padding:0 20px clamp(36px,5vw,64px)}
    @media(min-width:640px){.hero-body{padding:0 32px clamp(36px,5vw,64px)}}
    .hero-price-badge{display:inline-block;padding:14px 20px;border:1px solid rgba(184,151,90,.3);background:rgba(8,14,26,.55);backdrop-filter:blur(14px)}
    .ibtn{width:40px;height:40px;flex-shrink:0;display:flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.07);backdrop-filter:blur(8px);color:rgba(255,255,255,.75);transition:all .3s;cursor:pointer}
    .ibtn:hover{border-color:rgba(255,255,255,.5);color:#fff} .ibtn.fav{border-color:#f87171;background:rgba(248,113,113,.12);color:#f87171}
    .sec-light{background:${CR}} .sec-white{background:#fff} .sec-dark{background:${N}}
    .cw{max-width:1600px;margin:0 auto;padding:0 20px}
    @media(min-width:640px){.cw{padding:0 32px}}
    .sh-wrap{display:flex;align-items:flex-start;gap:20px;margin-bottom:40px}
    .sh-num{font-family:'Cormorant Garamond',serif;font-size:clamp(68px,8vw,110px);font-weight:300;color:rgba(0,0,0,.05);line-height:.8;flex-shrink:0;user-select:none}
    .sh-num-dark{color:rgba(255,255,255,.05)}
    .ov-grid{display:grid;grid-template-columns:1fr;gap:28px}
    @media(min-width:1024px){.ov-grid{grid-template-columns:1fr 320px;gap:52px}}
    .sb-sticky{position:static}
    @media(min-width:1024px){.sb-sticky{position:sticky;top:24px}}
    .inc-row{display:flex;align-items:flex-start;gap:11px;padding:9px 0;border-bottom:1px solid #f3efe9} .inc-row:last-child{border-bottom:none}
    .gallery-strip{display:grid;gap:2px;grid-template-columns:2fr 1fr;grid-template-rows:repeat(3,72px)}
    .gallery-strip .g0{grid-column:1;grid-row:1/4} .gallery-strip .g1{grid-column:2;grid-row:1} .gallery-strip .g2{grid-column:2;grid-row:2} .gallery-strip .g3{grid-column:2;grid-row:3}
    @media(max-width:640px){.gallery-strip{grid-template-columns:1fr 1fr;grid-template-rows:110px 72px}.gallery-strip .g0{grid-column:1/3;grid-row:1}.gallery-strip .g1{grid-column:1;grid-row:2}.gallery-strip .g2{grid-column:2;grid-row:2}.gallery-strip .g3{display:none}}
    .gcell{overflow:hidden;position:relative;cursor:zoom-in} .gcell img{width:100%;height:100%;object-fit:cover;transition:transform .8s cubic-bezier(.22,1,.36,1)} .gcell:hover img{transform:scale(1.05)}
    .sband{display:flex;flex-wrap:wrap} .scell{flex:1;min-width:100px;padding:24px 16px;text-align:center;border-right:1px solid rgba(184,151,90,.12);border-bottom:1px solid rgba(184,151,90,.12)} .scell:last-child{border-right:none}
    .pq-band{position:relative;overflow:hidden;background:${N};padding:clamp(48px,7vw,96px) 20px;display:flex;align-items:center;justify-content:center}
    @media(min-width:640px){.pq-band{padding:clamp(48px,7vw,96px) 32px}}
    .tl{border-left:1px solid ${GM};position:relative} .tl-step{display:flex;padding:0 0 32px 0} .tl-step:last-child{padding-bottom:0}
    .tl-icon-col{flex-shrink:0;width:28px;margin-left:-14px;margin-right:24px;display:flex;flex-direction:column;align-items:center}
    .tl-dot{width:28px;height:28px;border:1px solid ${G};background:${CR};display:flex;align-items:center;justify-content:center;flex-shrink:0}
    .tl-connector{flex:1;width:1px;background:linear-gradient(to bottom,${G},rgba(184,151,90,.05));margin-top:4px} .tl-step:last-child .tl-connector{display:none}
    .pill{font-family:'Jost',sans-serif;font-size:13px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;padding:4px 12px;border:1px solid #e2ddd6;color:#12100e;background:#fff;display:inline-flex}
    .pill-g{border-color:${GM};color:${G};background:${GD}}
    .gcard{border:1px solid #e8e4de;overflow:hidden;transition:border-color .3s} .gcard:hover{border-color:${G}}
    .gg{display:grid;grid-template-columns:1fr;gap:16px}
    @media(min-width:640px){.gg{grid-template-columns:1fr 1fr}}
    .rcard{border:1px solid #e8e4de;background:#fff;padding:24px;transition:border-color .3s,transform .3s} .rcard:hover{border-color:${G};transform:translateY(-2px)}
    .rg{display:grid;grid-template-columns:1fr;gap:16px}
    @media(min-width:640px){.rg{grid-template-columns:1fr 1fr}}
    @media(min-width:1024px){.rg{grid-template-columns:1fr 1fr 1fr}}
    .acard{border:1px solid #e8e4de;background:#fff;padding:18px 20px;display:flex;align-items:flex-start;justify-content:space-between;gap:14px;transition:border-color .3s} .acard.on{border-color:${G};background:${GD}}
    .atoggle{flex-shrink:0;width:30px;height:30px;border:1px solid #e8e4de;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .25s} .atoggle.on{border-color:${G};background:${G}}
    .fitem{border-bottom:1px solid #ece8e2} .fitem:first-child{border-top:1px solid #ece8e2}
    .fbtn{width:100%;display:flex;align-items:center;justify-content:space-between;padding:18px 0;background:transparent;cursor:pointer;gap:16px;text-align:left}
    .fbody{overflow:hidden;transition:max-height .4s cubic-bezier(.22,1,.36,1),opacity .3s;opacity:0;max-height:0} .fbody.open{opacity:1;max-height:260px}
    .gr{height:1px;background:linear-gradient(90deg,${G} 0%,rgba(184,151,90,.06) 100%)}
    .rbtn{position:relative;display:inline-flex;align-items:center;justify-content:center;gap:10px;font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;padding:16px 28px;border:1px solid ${G};background:${G};color:${N};overflow:hidden;transition:all .35s cubic-bezier(.22,1,.36,1);cursor:pointer;white-space:nowrap;text-decoration:none}
    .rbtn::before{content:'';position:absolute;inset:0;background:${N};transform:scaleX(0);transform-origin:left;transition:transform .35s cubic-bezier(.22,1,.36,1);z-index:0}
    .rbtn:hover::before{transform:scaleX(1)} .rbtn:hover{color:#fff;border-color:${N}} .rbtn span,.rbtn svg{position:relative;z-index:1}
    .glink{display:inline-flex;align-items:center;gap:8px;font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;padding:16px 22px;border:1px solid rgba(255,255,255,.2);color:rgba(255,255,255,.6);text-decoration:none;transition:all .3s}
    .glink:hover{border-color:rgba(255,255,255,.5);color:#fff}
    .bbar{position:fixed;bottom:0;left:0;right:0;z-index:200;background:rgba(8,14,26,.98);backdrop-filter:blur(20px);border-top:1px solid rgba(184,151,90,.2);padding:12px 20px;display:flex;align-items:center;justify-content:space-between;gap:12px;transform:translateY(100%);transition:transform .4s cubic-bezier(.22,1,.36,1)}
    @media(min-width:640px){.bbar{padding:14px 32px}}
    @media(min-width:900px){.bbar{left:72px}}
    .bbar.vis{transform:translateY(0)}
    .trow{display:flex;align-items:center;gap:11px;padding:9px 0;border-bottom:1px solid #f3efe9} .trow:last-child{border-bottom:none}
    .ticon{width:26px;height:26px;flex-shrink:0;border:1px solid ${GD};display:flex;align-items:center;justify-content:center}
    .wg{display:grid;grid-template-columns:1fr 1fr;gap:8px}
    @media(min-width:640px){.wg{grid-template-columns:repeat(4,1fr)}}
    .wcell{border:1px solid #e8e4de;background:#fff;padding:14px;transition:border-color .3s} .wcell:hover{border-color:${G}}
    .sl{display:flex;align-items:center;gap:10px;margin-bottom:10px}
    .sl-line{width:20px;height:1px;background:${G};flex-shrink:0}
    .sl-text{font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.2em;text-transform:uppercase;color:${G}}
    .cta-sec{position:relative;overflow:hidden;background:${N}}
    .lb{position:fixed;inset:0;z-index:20000;background:rgba(8,14,26,.97);backdrop-filter:blur(18px);display:flex;align-items:center;justify-content:center;padding:60px 20px 100px}
    .lb-img{max-width:100%;max-height:80vh;object-fit:contain;border:1px solid rgba(184,151,90,.18)}
    .lb-close{position:absolute;top:18px;right:18px;width:40px;height:40px;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.06);display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.8);cursor:pointer;transition:all .25s;font-size:22px;z-index:10020}
    .lb-close:hover{border-color:${G};color:${G}}
    .lb-arrow{position:absolute;top:50%;transform:translateY(-50%);width:42px;height:42px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.06);display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.75);cursor:pointer;transition:all .25s}
    .lb-arrow:hover{border-color:${G};color:${G}} .lb-prev{left:12px} .lb-next{right:12px}
    @media(min-width:640px){.lb-prev{left:28px} .lb-next{right:28px}}
    .lb-thumbs{position:absolute;bottom:20px;left:50%;transform:translateX(-50%);display:flex;gap:6px}
    .lb-thumb{width:52px;height:34px;object-fit:cover;border:1px solid rgba(255,255,255,.12);opacity:.45;cursor:pointer;transition:all .25s;flex-shrink:0}
    .lb-thumb:hover{opacity:.75} .lb-thumb.on{border-color:${G};opacity:1}
    .lb-label{position:absolute;top:18px;left:50%;transform:translateX(-50%);font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.2em;text-transform:uppercase;color:rgba(255,255,255,.35);white-space:nowrap}
    .rtier{border:1px solid;position:relative;overflow:hidden;margin-bottom:10px}
    .rtier-bar{position:absolute;top:0;left:0;width:3px;height:100%}
  `}</style>
);

const SL = ({ text, light }) => (
  <div className="sl">
    <div className="sl-line" style={light ? { background: 'rgba(184,151,90,.6)' } : {}} />
    <span className="sl-text" style={light ? { color: 'rgba(184,151,90,.7)' } : {}}>{text}</span>
  </div>
);

const Stars = ({ r, sz = 11 }) => (
  <div style={{ display: 'flex', gap: '2px' }}>
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={sz} fill={i<=Math.round(r)?G:'none'} stroke={i<=Math.round(r)?G:'#cbd5e1'} strokeWidth={1.5} />
    ))}
  </div>
);

const RefundTier = ({ tier }) => {
  const isGreen  = tier.tierType === 'full';
  const isOrange = tier.tierType === 'partial';
  const color  = isGreen ? '#22c55e' : isOrange ? G : '#ef4444';
  const bg     = isGreen ? 'rgba(34,197,94,0.07)' : isOrange ? GD : 'rgba(239,68,68,0.07)';
  const border = isGreen ? 'rgba(34,197,94,0.25)' : isOrange ? GM : 'rgba(239,68,68,0.2)';
  const Icon   = isGreen ? CheckCircle2 : isOrange ? AlertCircle : XCircle;
  return (
    <div className="rtier" style={{ borderColor: border, background: bg }}>
      <div className="rtier-bar" style={{ background: color }} />
      <div style={{ padding: '14px 14px 14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <Icon size={16} strokeWidth={1.5} style={{ color, flexShrink: 0 }} />
            <span className="fj" style={{ fontSize: '15px', fontWeight: 400, color: '#12100e' }}>{tier.windowLabel}</span>
          </div>
          <div style={{ background: color, padding: '4px 12px', flexShrink: 0 }}>
            <span className="fc" style={{ fontSize: '27px', fontWeight: 300, color: '#fff', lineHeight: 1 }}>{tier.refundDisplay}</span>
          </div>
        </div>
        <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.65, paddingLeft: '26px' }}>{tier.description}</p>
      </div>
    </div>
  );
};

const CHAPTERS = ['overview','details','guides','reviews','faqs'];

/* ─── Additional-cost groups (mirrors TourAdditionalCost.COST_TYPES) ─── */
const COST_GROUPS = [
  { key: 'required', label: 'Required' },
  { key: 'optional', label: 'Optional Upgrades' },
  { key: 'personal', label: 'Personal / Discretionary' },
];

const fmtDate = (v) => {
  if (!v) return '';
  try {
    return new Date(v).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  } catch { return ''; }
};

export default function TourExpedition() {
  const { id: slug } = useParams();
  const [fav,      setFav]      = useState(false);
  const [lbIdx,    setLbIdx]    = useState(null);
  const [faqI,     setFaqI]     = useState(null);
  const [barVis,   setBarVis]   = useState(false);
  const [active,   setActive]   = useState('overview');
  const [slideIdx, setSlideIdx] = useState(0);
  const heroRef = useRef(null);

  const { data, loading, error } = useQuery(GET_TOUR_EXPEDITION, {
    variables: { slug },
    fetchPolicy: 'cache-first',
  });

  const tour = data?.tour;
  const images = tour ? (
    tour.galleryImages?.length > 0 ? tour.galleryImages : [tour.featuredImage].filter(Boolean)
  ) : [];

  const [trackPageView] = useMutation(TRACK_PAGE_VIEW);
  useEffect(() => {
    if (!tour?.id) return;
    trackPageView({
      variables: {
        pageUrl: window.location.pathname,
        sessionId: getVisitorSessionId(),
        pageTitle: tour.name,
        tourId: tour.id,
      },
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour?.id]);

  // Hero auto-slide
  useEffect(() => {
    if (images.length > 1) {
      const t = setInterval(() => setSlideIdx(i => (i + 1) % images.length), 4500);
      return () => clearInterval(t);
    }
  }, [images.length]);

  // Lightbox keyboard
  useEffect(() => {
    const fn = e => {
      if (lbIdx === null) return;
      if (e.key === 'ArrowRight') setLbIdx(i => (i+1) % images.length);
      if (e.key === 'ArrowLeft')  setLbIdx(i => (i-1+images.length) % images.length);
      if (e.key === 'Escape')     setLbIdx(null);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [lbIdx, images.length]);

  // Sticky bar
  useEffect(() => {
    const fn = () => {
      if (heroRef.current) setBarVis(window.scrollY > heroRef.current.offsetHeight - 80);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = s => {
    document.getElementById(`ch-${s}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(s);
  };

  if (loading) return (
    <div className="fj" style={{ background: CR, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <S />
      <p className="fj" style={{ fontSize: '15px', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#12100e' }}>Loading safari…</p>
    </div>
  );

  if (error || !tour) return (
    <div className="fj" style={{ background: CR, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <S />
      <p className="fj" style={{ fontSize: '15px', color: '#f87171' }}>Tour not found.</p>
    </div>
  );

  const price    = Math.round(tour.currentPrice || tour.agencyServiceFee || 0);
  const guideAssignments = tour.guideAssignments || [];
  const primaryAssignment = guideAssignments.find(a => a.isPrimary) || guideAssignments[0];
  const primaryGuide = primaryAssignment?.guide;

  // Optional / required / personal extras — informational, staff add them to
  // invoices manually. Grouped by costType for display.
  const additionalCosts = tour.additionalCosts || [];
  const reviews = tour.reviews || [];
  const faqs = (tour.faqs || []).filter(f => f.question && f.answer);
  const ratingLabel = Number(tour.averageRating || 0).toFixed(1);

  // Drop rail/nav entries whose section renders nothing.
  const chapters = CHAPTERS.filter(c =>
    c === 'reviews' ? reviews.length > 0
    : c === 'faqs'  ? faqs.length > 0
    : true
  );

  const itineraryDays = tour.itineraryDays || [];

  const countryNames = tour.parks
    ?.map(p => p.country?.name).filter(Boolean)
    .filter((v,i,a) => a.indexOf(v) === i).join(', ') || '';

  return (
    <div className="fj" style={{ background: CR }}>
      <S />

      <div className="page-shell">

        {/* ── Rail ── */}
        <aside className="chapter-rail">
          <span className="rail-logo">Expedition</span>
          <div className="rail-dots">
            {chapters.map((ch, i) => (
              <React.Fragment key={ch}>
                {i > 0 && <div className="rail-connector" />}
                <div className={`rail-dot-wrap${active===ch?' active':''}`} data-label={ch} onClick={() => scrollTo(ch)}>
                  <div className="rail-dot" />
                </div>
              </React.Fragment>
            ))}
          </div>
          <span className="rail-num">{(Math.max(chapters.indexOf(active),0)+1).toString().padStart(2,'0')}</span>
        </aside>

        <main className="main">

          {/* Mobile nav */}
          <nav className="mob-nav">
            <div className="mob-nav-inner">
              {chapters.map(ch => (
                <button key={ch} onClick={() => scrollTo(ch)} className={`mnav-btn${active===ch?' on':''}`}>{ch}</button>
              ))}
            </div>
          </nav>

          {/* ════════ HERO ════════ */}
          <div ref={heroRef} className="hero">
            {images.map((src, i) => (
              <img key={src} src={src} alt={tour.name} className="hero-img"
                style={{ opacity: i===slideIdx ? 0.42 : 0, transition: 'opacity 1.2s cubic-bezier(0.4,0,0.2,1)' }} />
            ))}
            <div className="hero-grad" />
            <div className="hero-grain" />

            {/* Top bar */}
            <div className="hero-topbar">
              <Link to={`/tours/${slug}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 14px', border: '1px solid rgba(255,255,255,.18)', background: 'rgba(255,255,255,.07)', backdropFilter: 'blur(8px)', color: 'rgba(255,255,255,.8)', textDecoration: 'none', fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', transition: 'all .3s' }}>
                <ChevronLeft size={13} strokeWidth={1.5} /> Safari details
              </Link>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="ibtn"><Share2 size={14} strokeWidth={1.5} /></button>
                <button onClick={() => setFav(!fav)} className={`ibtn${fav?' fav':''}`}>
                  <Heart size={14} strokeWidth={1.5} fill={fav?'currentColor':'none'} />
                </button>
              </div>
            </div>

            {/* Hero copy */}
            <div className="hero-body">
              <div className="ri" style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <span className="fj" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: G }}>
                  {tour.category?.name || 'Safari'}
                </span>
                <div style={{ width: '1px', height: '11px', background: 'rgba(255,255,255,.2)' }} />
                <Stars r={tour.averageRating} />
                <span className="fj" style={{ fontSize: '13px', color: 'rgba(255,255,255,.4)' }}>({tour.totalReviews} reviews)</span>
              </div>

              <h1 className="fc ri d1" style={{ fontSize: 'clamp(40px,7.5vw,92px)', fontWeight: 300, fontStyle: 'normal', color: '#fff', lineHeight: .88, marginBottom: '16px', maxWidth: '800px' }}>
                {tour.name}
              </h1>

              <p className="fj ri d2" style={{ fontSize: '13px', fontWeight: 300, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.38)', marginBottom: '28px' }}>
                {tour.tagline}
              </p>

              <div className="ri d3" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                  {[
                    { I: MapPin, v: countryNames },
                    { I: Clock,  v: tour.durationLabel || `${tour.durationDays} Days` },
                    { I: Users,  v: `Max ${tour.maxParticipants} Guests` },
                  ].filter(x => x.v).map(({ I, v }) => (
                    <div key={v} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <I size={12} strokeWidth={1.5} style={{ color: G }} />
                      <span className="fj" style={{ fontSize: '15px', color: 'rgba(255,255,255,.72)' }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div className="hero-price-badge">
                  <p className="fj" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: G, marginBottom: '4px' }}>Price from</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span className="fc" style={{ fontSize: 'clamp(40px,4.5vw,58px)', fontWeight: 300, color: '#fff', lineHeight: 1 }}>${price.toLocaleString()}</span>
                    <span className="fj" style={{ fontSize: '13px', color: 'rgba(255,255,255,.32)' }}>USD / Person</span>
                  </div>
                </div>
              </div>

              {/* Slide dots */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                {images.map((_, i) => (
                  <button key={i} onClick={() => setSlideIdx(i)} style={{ width: i===slideIdx?'24px':'6px', height: '6px', background: i===slideIdx?G:'rgba(255,255,255,0.35)', border: 'none', cursor: 'pointer', transition: 'all 0.4s cubic-bezier(.22,1,.36,1)', padding: 0, flexShrink: 0 }} />
                ))}
              </div>
            </div>
          </div>

          {/* Gallery strip */}
          <div className="gallery-strip">
            {images.slice(0,4).map((src, i) => (
              <div key={i} className={`gcell g${i}`} onClick={() => setLbIdx(i)}>
                <img src={src} alt={`View ${i+1}`} />
                {i === 0 && (
                  <div style={{ position: 'absolute', bottom: '8px', left: '8px' }}>
                    <span className="fj pill pill-g" style={{ fontSize: '12px' }}>
                      <Camera size={9} strokeWidth={1.5} style={{ marginRight: '5px' }} />{images.length} Photos
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ════════ 01 OVERVIEW ════════ */}
          <section id="ch-overview" className="sec-light" style={{ padding: '64px 0 56px' }}>
            <div className="cw">
              <div className="sh-wrap">
                <span className="sh-num">01</span>
                <div style={{ paddingTop: '8px' }}>
                  <SL text="The Safari" />
                  <h2 className="fc" style={{ fontSize: 'clamp(30px,3.5vw,42px)', fontStyle: 'normal', fontWeight: 300, color: N, lineHeight: 1.1 }}>
                    {/* Was a hard-coded "Nine Days" on every safari, whatever its length. */}
                    {(['One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen','Twenty'][tour.durationDays - 1] || tour.durationDays)}{' '}
                    {tour.durationDays === 1 ? 'Day' : 'Days'} in the Bush —<br />No Two Ever the Same
                  </h2>
                </div>
              </div>

              <div className="ov-grid">
                <div>
                  {/* Description */}
                  {tour.description.split('\n\n').map((p, i) => (
                    <p key={i} className="fj" style={{ fontSize: '16px', fontWeight: 300, lineHeight: 1.9, color: '#12100e', marginBottom: '16px' }}>{p}</p>
                  ))}

                  <div className="gr" style={{ margin: '28px 0 24px' }} />

                  {/* Differentiators */}
                  {(() => {
                      const differentiators = (tour.differentiators || []).map(d =>
                        typeof d === 'string' ? JSON.parse(d) : d
                      );
                      return differentiators.length > 0 && (
                        <>
                          <SL text="Why This Is Different" />
                          <div style={{ borderLeft: `1px solid #f0ece6`, marginBottom: '28px' }}>
                            {differentiators.map((item, i) => (
                              <div key={i} style={{ paddingLeft: '24px', paddingBottom: '20px', paddingTop: i===0?'0':'20px', borderBottom: i < differentiators.length-1 ? '1px solid #f5f2ee' : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                  <div style={{ width: '6px', height: '6px', background: G, transform: 'rotate(45deg)', flexShrink: 0 }} />
                                  <h4 className="fc text-slate-900 font-light" style={{ fontSize: '25px', color: N }}>{item.title}</h4>
                                </div>
                                <p className="fj" style={{ fontSize: '15px', color: '#12100e', fontWeight: 300, lineHeight: 1.9 }}>{item.body}</p>
                              </div>
                            ))}
                          </div>
                          <div className="gr" style={{ margin: '0 0 24px' }} />
                        </>
                      );
                    })()}
                  {/* Highlights as elevate options */}
                  {(tour.highlightAssignments || []).length > 0 && (
                    <>
                      <SL text="What's Included" />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                        {tour.highlightAssignments.map(({ highlight }) => {
                          const Icon = getIcon(highlight.iconName);
                          return (
                            <div key={highlight.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 16px', border: `1px solid #e8e4de`, background: '#fff' }}>
                              <div style={{ width: '32px', height: '32px', border: `1px solid ${GD}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Icon size={13} strokeWidth={1.5} style={{ color: G }} />
                              </div>
                              <div>
                                <p className="fj" style={{ fontSize: '15px', fontWeight: 600, color: '#12100e', marginBottom: '2px' }}>{highlight.label}</p>
                                <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.5 }}>{highlight.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Seasonal conditions — static for now, can be park-based in future */}
                  <div className="gr" style={{ margin: '0 0 22px' }} />
                  <SL text="Seasonal Conditions" />
                  <div className="wg" style={{ marginTop: '8px' }}>
                    {[
                      { season: 'Jun — Jul', Icon: Wind,       condition: 'Dry season begins', temp: '17–27°C' },
                      { season: 'Aug — Oct', Icon: Sunrise,    condition: 'Peak dry & clear',  temp: '20–28°C' },
                      { season: 'Nov — Dec', Icon: TrendingUp, condition: 'Short rains',       temp: '20–28°C' },
                      { season: 'Jan — Mar', Icon: Umbrella,   condition: 'Calving season',    temp: '22–30°C' },
                    ].map(({ season, Icon, condition, temp }) => (
                      <div key={season} className="wcell">
                        <Icon size={13} strokeWidth={1.5} style={{ color: G, display: 'block', marginBottom: '8px' }} />
                        <p className="fj" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#12100e', marginBottom: '2px' }}>{season}</p>
                        <p className="fc" style={{ fontSize: '25px', fontWeight: 300, color: '#12100e', lineHeight: 1 }}>{temp}</p>
                        <p className="fj" style={{ fontSize: '13px', color: '#12100e', marginTop: '2px' }}>{condition}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sticky sidebar */}
                <div className="sb-sticky">
                  <div style={{ border: '1px solid #e8e4de', background: '#fff', marginBottom: '10px' }}>
                    <div style={{ height: '2px', background: `linear-gradient(90deg,${G},rgba(184,151,90,.08))` }} />
                    <div style={{ padding: '22px' }}>
                      <p className="fj" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: G, marginBottom: '14px' }}>What's Included</p>
                      {(tour.includes || []).map(text => (
                        <div key={text} className="inc-row">
                          <CheckCircle2 size={12} strokeWidth={1.5} style={{ color: G, flexShrink: 0, marginTop: '1px' }} />
                          <span className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e' }}>{text}</span>
                        </div>
                      ))}
                      {(tour.excludes || []).map(text => (
                        <div key={text} className="inc-row">
                          <XCircle size={12} strokeWidth={1.5} style={{ color: '#e2e8f0', flexShrink: 0, marginTop: '1px' }} />
                          <span className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#c5d0dc', textDecoration: 'line-through' }}>{text}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid #f0ece6' }}>
                        <Link to={`/booking/${tour.id}`} className="rbtn" style={{ width: '100%' }}>
                          <span>Reserve Your Place</span><ArrowRight size={12} />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div style={{ border: '1px solid #e8e4de', background: '#FAFAF8', padding: '14px 18px' }}>
                    {[
                      { I: Zap,         l: 'Instant Confirmation' },
                      { I: Calendar,    l: 'Free Date Changes' },
                      { I: ShieldCheck, l: tour.cancellationPolicy?.name || '30-Day Free Cancellation' },
                    ].map(({ I, l }) => (
                      <div key={l} className="trow">
                        <div className="ticon"><I size={11} strokeWidth={1.5} style={{ color: G }} /></div>
                        <span className="fj" style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#12100e' }}>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stat band */}
          <div className="dark-sec" style={{ background: N }}>
            <div className="cw">
              <div className="sband" style={{ border: 'none', borderTop: '1px solid rgba(184,151,90,.1)', borderBottom: '1px solid rgba(184,151,90,.1)' }}>
                {[
                  [`${ratingLabel} ★`, `${tour.totalReviews} Reviews`],
                  [tour.durationLabel || `${tour.durationDays} Days`, 'Duration'],
                  [`Max ${tour.maxParticipants}`, 'Per Vehicle'],
                  [`${tour.parks?.length || 6}`, 'Ecosystems'],
                  [primaryGuide ? `${primaryGuide.experienceYears} yrs` : '20+ yrs', 'Lead Guide Exp.'],
                ].map(([v, l]) => (
                  <div key={l} className="scell">
                    <p className="fc" style={{ fontSize: 'clamp(24px,2.5vw,30px)', fontWeight: 300, color: '#fff', lineHeight: 1, marginBottom: '5px' }}>{v}</p>
                    <p className="fj" style={{ fontSize: '12px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.28)' }}>{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pull quote */}
          {tour.pullQuote && (
            <div className="pq-band">
              <div style={{ backgroundImage: GRAIN, position: 'absolute', inset: 0, opacity: .5, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,rgba(8,14,26,.97),rgba(8,14,26,.88))' }} />
              <div style={{ position: 'relative', zIndex: 1, maxWidth: '660px', textAlign: 'center', padding: '0 20px' }}>
                <div style={{ width: '30px', height: '1px', background: G, margin: '0 auto 24px' }} />
                <p className="fc" style={{ fontSize: 'clamp(26px,3.5vw,38px)', fontStyle: 'normal', fontWeight: 300, color: '#fff', lineHeight: 1.5, marginBottom: '24px', whiteSpace: 'pre-line' }}>
                  {tour.pullQuote}
                </p>
                <p className="fj" style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,.35)' }}>
                  {tour.pullQuoteBy}
                </p>
              </div>
            </div>
          )}

          {/* ════════ 02 DETAILS ════════ */}
          <section id="ch-details" className="sec-white" style={{ padding: '64px 0' }}>
            <div className="cw">
              <div className="sh-wrap">
                <span className="sh-num">02</span>
                <div style={{ paddingTop: '8px' }}>
                  <SL text="Safari Details" />
                  <h2 className="fc" style={{ fontSize: 'clamp(30px,3.5vw,42px)', fontStyle: 'normal', fontWeight: 300, color: N, lineHeight: 1.1 }}>
                    Your Journey, Day by Day
                  </h2>
                </div>
              </div>

              {/* Cancellation policy */}
              {tour.cancellationPolicy && (
                <div style={{ marginBottom: '48px' }}>
                  <p className="fj" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: G, marginBottom: '20px' }}>Cancellation Policy — {tour.cancellationPolicy.name}</p>
                  <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.75, marginBottom: '16px' }}>{tour.cancellationPolicy.description}</p>
                  {(tour.cancellationPolicy.tiers || []).map(tier => <RefundTier key={tier.id} tier={tier} />)}
                  {tour.cancellationPolicy.freeDateChanges && (
                    <div style={{ padding: '14px 18px', background: GD, border: `1px solid ${GM}`, marginTop: '16px' }}>
                      <p className="fj" style={{ fontSize: '15px', color: '#12100e', fontWeight: 300, lineHeight: 1.65 }}>
                        <strong className="fj" style={{ fontWeight: 600, color: G }}>Free date changes — always. </strong>
                        {tour.cancellationPolicy.freeDateChangesNote}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Itinerary timeline */}
              <SL text="Step by Step" />
              <h3 className="fc" style={{ fontSize: 'clamp(24px,2.5vw,30px)', fontStyle: 'normal', fontWeight: 300, color: N, lineHeight: 1.1, margin: '8px 0 28px' }}>
                Day by Day
              </h3>
              <div className="tl" style={{ maxWidth: '680px' }}>
                {itineraryDays.map((day, i) => {
                  const CatIcon = CAT_ICONS[day.category] || Compass;
                  return (
                    <div key={day.id} className="tl-step">
                      <div className="tl-icon-col">
                        <div className="tl-dot"><CatIcon size={12} strokeWidth={1.5} style={{ color: G }} /></div>
                        <div className="tl-connector" />
                      </div>
                      <div style={{ flex: 1, paddingBottom: '8px' }}>
                        <span className="fj" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: G, display: 'block', marginBottom: '6px' }}>
                          {day.timeLabel}
                        </span>
                        <h3 className="fc" style={{ fontSize: 'clamp(22px,2.5vw,24px)', fontStyle: 'normal', fontWeight: 300, color: N, marginBottom: '8px', lineHeight: 1.1 }}>{day.title}</h3>
                        <p className="fj" style={{ fontSize: '15px', fontWeight: 300, lineHeight: 1.8, color: '#12100e', marginBottom: '12px' }}>{day.description}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {(day.includesAtStop || []).map(t => <span key={t} className="fj pill">{t}</span>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Optional extras & additional costs */}
              {additionalCosts.length > 0 && (
                <div style={{ marginTop: '52px' }}>
                  <SL text="Good to Know" />
                  <h3 className="fc" style={{ fontSize: 'clamp(24px,2.5vw,30px)', fontStyle: 'normal', fontWeight: 300, color: N, lineHeight: 1.1, margin: '8px 0 28px' }}>
                    Optional Extras &amp; Additional Costs
                  </h3>
                  {COST_GROUPS.map(({ key, label }) => {
                    const rows = additionalCosts.filter(c => c.costType === key);
                    if (rows.length === 0) return null;
                    return (
                      <div key={key} style={{ marginBottom: '26px', maxWidth: '680px' }}>
                        <p className="fj" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: G, marginBottom: '12px' }}>{label}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {rows.map(c => (
                            <div key={c.id} className="acard">
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1 }}>
                                {c.icon && <span style={{ fontSize: '22px', lineHeight: 1.1, flexShrink: 0 }}>{c.icon}</span>}
                                <div>
                                  <p className="fj" style={{ fontSize: '15px', fontWeight: 600, color: '#12100e', marginBottom: (c.description || c.note) ? '4px' : 0 }}>{c.name}</p>
                                  {c.description && <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.6 }}>{c.description}</p>}
                                  {c.note && <p className="fj" style={{ fontSize: '13px', fontWeight: 400, color: G, marginTop: '3px' }}>{c.note}</p>}
                                </div>
                              </div>
                              {(c.priceDisplay || c.pricePer) && (
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  {c.priceDisplay && <p className="fc" style={{ fontSize: '25px', fontWeight: 300, color: N, lineHeight: 1 }}>{c.priceDisplay}</p>}
                                  {c.pricePer && <p className="fj" style={{ fontSize: '13px', color: '#12100e', letterSpacing: '.1em', marginTop: '2px' }}>{c.pricePer}</p>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* ════════ 03 GUIDES ════════ */}
          <section id="ch-guides" className="sec-dark dark-sec" style={{ padding: '64px 0' }}>
            <div className="cw">
              <div className="sh-wrap">
                <span className="sh-num sh-num-dark">03</span>
                <div style={{ paddingTop: '8px' }}>
                  <SL text="Who Leads You" light />
                  <h2 className="fc" style={{ fontSize: 'clamp(30px,3.5vw,42px)', fontStyle: 'normal', fontWeight: 300, color: '#fff', lineHeight: 1.1 }}>
                    Meet Your Field Team
                  </h2>
                </div>
              </div>
              <div className="gg">
                {guideAssignments.map(({ guide, roleDisplay, isPrimary }) => (
                  <div key={guide.id} className="gcard" style={{ background: 'rgba(255,255,255,.04)', borderColor: 'rgba(255,255,255,.08)' }}>
                    {guide.photo && (
                      <div style={{ height: '260px', overflow: 'hidden', position: 'relative' }}>
                        <img src={guide.photo} alt={guide.user?.fullName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', filter: 'grayscale(25%)', transition: 'transform .7s,filter .5s' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.filter = 'grayscale(0%)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'grayscale(25%)'; }}
                        />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(8,14,26,.85) 0%,transparent 50%)' }} />
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '18px 20px' }}>
                          <p className="fj" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: G, marginBottom: '3px' }}>{roleDisplay}</p>
                          <h3 className="fc" style={{ fontSize: '29px', fontStyle: 'normal', fontWeight: 300, color: '#fff' }}>
                            {guide.user?.fullName || `${guide.user?.firstName} ${guide.user?.lastName}`}
                          </h3>
                        </div>
                      </div>
                    )}
                    <div style={{ padding: '20px' }}>
                      <div style={{ height: '1px', background: `linear-gradient(90deg,${G},transparent)`, marginBottom: '16px' }} />
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' }}>
                        <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: 'rgba(255,255,255,.5)', lineHeight: 1.75, flex: 1 }}>
                          {guide.bio || guide.shortBio}
                        </p>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <p className="fc" style={{ fontSize: '29px', fontWeight: 300, color: '#fff' }}>{guide.experienceYears}</p>
                          <p className="fj" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,.28)' }}>yrs exp.</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {(guide.languages || []).map(l => (
                          <span key={l} className="fj pill" style={{ background: 'transparent', borderColor: 'rgba(255,255,255,.12)', color: 'rgba(255,255,255,.45)' }}>
                            {langLabel(l)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════════ 04 REVIEWS ════════ */}
          {reviews.length > 0 && (
          <section id="ch-reviews" className="sec-light" style={{ padding: '64px 0' }}>
            <div className="cw">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px', marginBottom: '0' }}>
                <div className="sh-wrap" style={{ marginBottom: '0', flex: 1 }}>
                  <span className="sh-num">04</span>
                  <div style={{ paddingTop: '8px' }}>
                    <SL text="Guest Voices" />
                    <h2 className="fc" style={{ fontSize: 'clamp(30px,3.5vw,42px)', fontStyle: 'normal', fontWeight: 300, color: N, lineHeight: 1.1 }}>
                      From the Field
                    </h2>
                  </div>
                </div>
                <div style={{ textAlign: 'right', paddingBottom: '8px', flexShrink: 0 }}>
                  <p className="fc" style={{ fontSize: 'clamp(44px,5vw,60px)', fontWeight: 300, color: N, lineHeight: 1 }}>{ratingLabel}</p>
                  <Stars r={tour.averageRating} sz={13} />
                  <p className="fj" style={{ fontSize: '13px', color: '#12100e', marginTop: '4px', letterSpacing: '0.14em' }}>{tour.totalReviews} REVIEWS</p>
                </div>
              </div>
              <div className="gr" style={{ margin: '28px 0 32px' }} />
              <div className="rg">
                {reviews.map(rv => (
                  <div key={rv.id} className="rcard">
                    <Quote size={18} strokeWidth={1.5} style={{ color: G, opacity: 0.5, marginBottom: '10px' }} />
                    {rv.rating != null && <div style={{ marginBottom: '10px' }}><Stars r={Number(rv.rating)} sz={12} /></div>}
                    {rv.title && (
                      <p className="fc" style={{ fontSize: '21px', fontStyle: 'normal', fontWeight: 400, color: N, lineHeight: 1.3, marginBottom: '8px' }}>{rv.title}</p>
                    )}
                    <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.75, marginBottom: '14px' }}>{rv.body}</p>
                    {(rv.media || []).slice(0, 1).map(m => (
                      <img key={m.id} src={m.thumbnailUrl || m.url} alt={m.caption || ''}
                        style={{ width: '100%', height: '140px', objectFit: 'cover', marginBottom: '14px', border: '1px solid #e8e4de' }} />
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                      <div>
                        <p className="fj" style={{ fontSize: '15px', fontWeight: 600, color: '#12100e' }}>{rv.authorName}</p>
                        {rv.reviewedAt && (
                          <p className="fj" style={{ fontSize: '13px', color: '#12100e', letterSpacing: '.1em', marginTop: '1px' }}>{fmtDate(rv.reviewedAt)}</p>
                        )}
                      </div>
                      {rv.isVerifiedGuest && (
                        <span className="fj pill pill-g" style={{ fontSize: '12px' }}>
                          <ShieldCheck size={9} strokeWidth={1.5} style={{ marginRight: '4px' }} />Verified guest
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          )}

          {/* ════════ 05 FAQs ════════ */}
          {faqs.length > 0 && (
          <section id="ch-faqs" className="sec-light" style={{ padding: '64px 0' }}>
            <div className="cw">
              <div className="sh-wrap">
                <span className="sh-num">05</span>
                <div style={{ paddingTop: '8px' }}>
                  <SL text="Questions & Answers" />
                  <h2 className="fc" style={{ fontSize: 'clamp(30px,3.5vw,42px)', fontStyle: 'normal', fontWeight: 300, color: N, lineHeight: 1.1 }}>
                    Frequently Asked
                  </h2>
                </div>
              </div>
              <div style={{ maxWidth: '680px' }}>
                {faqs.map((f, i) => {
                  const open = faqI === i;
                  return (
                    <div key={f.id} className="fitem">
                      <button className="fbtn" onClick={() => setFaqI(open ? null : i)}>
                        <span className="fc" style={{ fontSize: 'clamp(20px,2vw,20px)', fontStyle: 'normal', fontWeight: 400, color: N, lineHeight: 1.35 }}>
                          {f.question}
                        </span>
                        {open
                          ? <Minus size={16} strokeWidth={1.5} style={{ color: G, flexShrink: 0 }} />
                          : <Plus size={16} strokeWidth={1.5} style={{ color: G, flexShrink: 0 }} />}
                      </button>
                      <div className={`fbody${open ? ' open' : ''}`}>
                        <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.8, padding: '0 0 18px' }}>
                          {f.answer}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          )}

          {/* ════════ CTA ════════ */}
          <div className="cta-sec">
            <div style={{ height: '2px', background: `linear-gradient(90deg,${G},rgba(184,151,90,.08))` }} />
            <div style={{ backgroundImage: GRAIN, position: 'absolute', inset: 0, opacity: .45, pointerEvents: 'none' }} />
            {images[1] && (
              <img src={images[1]} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: .09 }} />
            )}
            <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(52px,7vw,96px) 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <SL text="Secure Your Place" light />
              <h2 className="fc" style={{ fontSize: 'clamp(32px,5vw,64px)', fontStyle: 'normal', fontWeight: 300, color: '#fff', lineHeight: 1.02, marginBottom: '14px' }}>
                Ready to Follow<br />the Migration?
              </h2>
              <p className="fj" style={{ fontSize: '15px', fontWeight: 300, color: 'rgba(255,255,255,.38)', letterSpacing: '.12em', maxWidth: '400px', marginBottom: '36px' }}>
                Departures fill months in advance. Reserve today with instant confirmation.
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Link to={`/booking/${tour.id}`} className="rbtn">
                  <span>Reserve Your Place</span><ArrowRight size={12} />
                </Link>
                <Link to={`/tours/${slug}`} className="glink">Back to Expedition Details</Link>
              </div>
            </div>
          </div>

        </main>
      </div>

      {/* Sticky bar */}
      <div className={`bbar${barVis?' vis':''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0, overflow: 'hidden' }}>
          <div>
            <p className="fj" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: G, marginBottom: '2px' }}>Safari</p>
            <p className="fc" style={{ fontSize: '18px', fontStyle: 'normal', fontWeight: 300, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'clamp(110px,28vw,240px)' }}>
              {tour.name}
            </p>
          </div>
          <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,.08)', flexShrink: 0 }} />
          <div>
            <p className="fc" style={{ fontSize: 'clamp(22px,2.5vw,26px)', fontWeight: 300, color: '#fff', lineHeight: 1 }}>${price.toLocaleString()}</p>
            <p className="fj" style={{ fontSize: '12px', color: 'rgba(255,255,255,.3)', letterSpacing: '0.14em' }}>per person</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <Stars r={tour.averageRating} sz={10} />
          <Link to={`/booking/${tour.id}`} className="rbtn" style={{ padding: '11px 20px' }}>
            <span>Reserve Now</span><ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lbIdx !== null && (
        <div className="lb" onClick={() => setLbIdx(null)}>
          <button className="lb-close" onClick={e => { e.stopPropagation(); setLbIdx(null); }}>✕</button>
          <span className="lb-label fj">{lbIdx + 1} / {images.length}</span>
          <button className="lb-arrow lb-prev" onClick={e => { e.stopPropagation(); setLbIdx(i => (i-1+images.length)%images.length); }}>
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <img src={images[lbIdx]} alt={`View ${lbIdx+1}`} className="lb-img" onClick={e => e.stopPropagation()} />
          <button className="lb-arrow lb-next" onClick={e => { e.stopPropagation(); setLbIdx(i => (i+1)%images.length); }}>
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
          <div className="lb-thumbs" onClick={e => e.stopPropagation()}>
            {images.map((src, i) => (
              <img key={i} src={src} alt="" className={`lb-thumb${lbIdx===i?' on':''}`} onClick={() => setLbIdx(i)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}